import CircularProgress from "@material-ui/core/CircularProgress"
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { Elements, TextButton } from "@tfo/common"
import { useEffect, useState } from "react"
import { useIntl } from "react-intl"
import $http from "../../utils/$http"
import { usePolling } from "./usePolling"
import bank from '../../media/images/BankID_logo_white.png'

import "./BankIdSign.scss"

const { Heading4, Paragraph } = Elements

export type BankIdSignProps = {
    orderId: number,
    onCancel: () => void
    onComplete: () => void
    onError: (message: string) => void
}

type SignResponseOtherDevice = {
    qrCodeBase64Encoded: string
    qrStartTime: Date
}

type SignResponseSameDevice = {
    launchUrl: string
    deviceMightRequireUserInteractionToLaunchBankIdApp: boolean
    deviceWillReloadPageOnReturnFromBankIdApp: boolean
}

type QrPromptProps = {
    qrCodeBase64: string
    qrStartTime: Date
}

type QrCodeResponse = {
    qrCodeBase64Encoded: string
}

type CollectResponse = {
    status: CollectStatus
    statusMessage: string
}

enum CollectStatus {
    Pending = "Pending",
    Complete = "Complete",
    Failure = "Failure",
    Retry = "Retry",
    Unknown = "Unknown",
}

const everySecond = 1000
const everyTwoSeconds = 2000

export const BankIdSign = ({ orderId, onCancel, onComplete, onError }: BankIdSignProps) => {
    const intl = useIntl()
    const bankIdIcon = () => <img className="bankId-icon" src={bank} alt="BankId Logo"></img>

    const isMobile = /Mobi|Android/i.test(navigator.userAgent)
    const returnUrl = `${window.location.origin}${window.location.pathname}#initiated=true`

    const [isStarting, setIsStarting] = useState<boolean>(true)
    const [qrCode, setQrCode] = useState<QrPromptProps | null>(null)
    const [polling, setPolling] = useState<{ qrInterval: number | null, collectInterval: number | null }>({ qrInterval: null, collectInterval: null })
    const [statusMessage, setStatusMessage] = useState<string | null>(null)
    const [signOnSameDevice, setSignOnSameDevice] = useState<boolean>(isMobile)
    const [launchUrl, setLaunchUrl] = useState<string | null>(null)

    const isInitiated = new URLSearchParams(window.location.hash.slice(1)).get("initiated") === "true"

    const stopPolling = () => {
        setPolling({ qrInterval: null, collectInterval: null })
    }
    const startPolling = () => {
        setPolling({ qrInterval: everySecond, collectInterval: everyTwoSeconds })
    }

    usePolling(async () => {
        try {
            const { qrCodeBase64Encoded } = await $http.post<QrCodeResponse>(`bankid/${orderId}/qr-code`, { qrStartTime: qrCode?.qrStartTime })
            setQrCode((prev) => ({ ...prev, qrCodeBase64: qrCodeBase64Encoded }))
        } catch (error) {
            console.error("Failed to fetch QR code:", error)
        }
    }, polling.qrInterval)

    usePolling(async () => {
        try {
            const response = await $http.post<CollectResponse>(`bankid/${orderId}/collect`, { sameDevice: isMobile })
            switch (response.status) {
                case CollectStatus.Complete:
                    stopPolling()
                    onComplete()
                    setStatusMessage(null)
                    break;
                case CollectStatus.Failure:
                    stopPolling()
                    onError(response.statusMessage)
                    setStatusMessage(null)
                    setQrCode(null)
                    break;
                case CollectStatus.Retry:
                    stopPolling()
                    onError(response.statusMessage)
                    setStatusMessage(null)
                    setQrCode(null)
                    break;
                case CollectStatus.Unknown:
                    stopPolling()
                    onError(response.statusMessage)
                    setStatusMessage(null)
                    setQrCode(null)
                    break;
                case CollectStatus.Pending:
                    setStatusMessage(response.statusMessage)
                    break;
            }
        } catch (error) {
            console.error("Failed to collect status:", error)
        }
    }, polling.collectInterval)

    const cancelSigning = async () => {
        try {
            await $http.post(`bankid/${orderId}/cancel`, {})
        } catch (error) {
            console.error("Failed to cancel signing:", error)
        }
        finally {
            stopPolling()
            onCancel()
        }
    }

    const startSigningOtherDevice = async (orderId: number, returnUrl: string) => {
        try {
            const { qrCodeBase64Encoded, qrStartTime } = await $http.post<SignResponseOtherDevice>(`bankid/${orderId}/start/other-device`, { returnUrl })

            setStatusMessage(statusMessage)
            setQrCode({ qrCodeBase64: qrCodeBase64Encoded, qrStartTime })
            startPolling()
        } catch (error) {
            console.error("Failed to start signing:", error)
        } finally {
            setIsStarting(false)
        }
    }

    const startSigningSameDevice = async (orderId: number, returnUrl: string) => {
        try {
            const { launchUrl, deviceMightRequireUserInteractionToLaunchBankIdApp, deviceWillReloadPageOnReturnFromBankIdApp } = await $http.post<SignResponseSameDevice>(`bankid/${orderId}/start/same-device`, { returnUrl })

            if (deviceMightRequireUserInteractionToLaunchBankIdApp) {
                setLaunchUrl(launchUrl)
            } else {
                window.location.replace(launchUrl)
            }

            startPolling()
        } catch (error) {
            console.error("Failed to start signing:", error)
        } finally {
            setIsStarting(false)
        }
    }

    const onSwitchDevice = () => {
        setSignOnSameDevice(false)
        startSigningOtherDevice(orderId, returnUrl)
    }

    useEffect(() => {
        if (!signOnSameDevice) {
            startSigningOtherDevice(orderId, returnUrl)
        } else {
            setIsStarting(false)
        }
    }, [orderId])

    if (isInitiated && polling.collectInterval === null) {
        startPolling()
        setIsStarting(false)
    }

    return (isStarting
        ? <div className='bankid-sign-container progressIcon'>
            <CircularProgress style={{ color: '#009B48' }} />
        </div>
        : <div className="bankid-sign-container">
            {<Heading4 style={{ color: 'rgb(0, 155, 72)' }}>{intl.formatMessage({ id: "startIndividual.bankid.heading" })}</Heading4>}
            {signOnSameDevice ?
                <>
                    <TextButton
                        className="btn-start"
                        onClick={() => startSigningSameDevice(orderId, returnUrl)}
                        icon={bankIdIcon()}
                        disabled={launchUrl !== null}>
                        {intl.formatMessage({
                            id: 'startIndividual.bankid.btn.start'
                        })}
                    </TextButton>
                    {launchUrl &&
                        <TextButton
                            className="btn-start"
                            onClick={() => window.location.replace(launchUrl)}>
                            {intl.formatMessage({ id: 'startIndividual.bankid.btn.openApp' })}
                        </TextButton>
                    }
                    <div className="bankid-sign-switch-device">
                        <Paragraph>{intl.formatMessage({ id: "startIndividual.bankid.otherDevice" })}</Paragraph>
                        <button className="link-button" onClick={() => onSwitchDevice()}>{intl.formatMessage({ id: "startIndividual.bankid.otherDevice.btn" })}</button>
                    </div>
                </>
                :
                qrCode && <QRPrompt {...qrCode} />}
            {statusMessage && <div className="bankid-sign-instruction">
                <Paragraph>{statusMessage}</Paragraph>
            </div>}
            {isMobile && !signOnSameDevice && <div className="bankid-sign-switch-device">
                <Paragraph>{intl.formatMessage({ id: "startIndividual.bankid.sameDevice" })}</Paragraph>
                <button className="link-button" onClick={() => onSwitchDevice()}>{intl.formatMessage({ id: "startIndividual.bankid.sameDevice.btn" })}</button>
            </div>
            }
            <div className="bankid-sign-information">
                <InfoOutlinedIcon />
                <Paragraph style={{ fontSize: '14px', lineHeight: '2.1rem' }}>{intl.formatMessage({ id: "startIndividual.bankid.information" })}</Paragraph>
            </div>
            {onCancel && <TextButton
                secondary
                className="btn-cancel"
                onClick={cancelSigning}>
                {intl.formatMessage({
                    id: 'startIndividual.bankid.btn.cancel',
                })}
            </TextButton>}
        </div>)
}

export const QRPrompt: React.FC<QrPromptProps> = ({ qrCodeBase64 }) => {
    return (
        <>
            {qrCodeBase64 &&
                <div style={{ display: 'flex', justifyContent: 'center' }} >
                    <img style={{ width: '20rem', height: '20rem' }} src={'data:image/png;base64, ' + qrCodeBase64} alt="QR Code" ></img>
                </div>}
        </>
    )
}